import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { AdvancedCustomSurveyAudienceFilterApiComponent } from 'views/Widgets/Filters/AdvancedSpecializedFilter';
import { AdvancedCurrentPeopleFilterRoleUserApiComponent } from 'views/Widgets/Filters/AdvancedSpecializedFilter/highOrder';
import { type ApiGroup } from 'views/Widgets/Filters/AdvancedSpecializedFilter/types';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';
import {
  type CustomSurveyReportData,
  REPORT_SPLIT_TYPE_OPTIONS,
  type ReportSplitOptions,
  type ReportSplitType,
  validateCustomSurveyReport,
} from '../consts';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  surveyId: number;
  // Fetched from custom-survey/surveys/{surveyId}/report-split-options by
  // the caller (CustomSurveyReportsTab), so the modal itself stays a pure
  // form - it neither fetches nor persists anything.
  splitOptions: ReportSplitOptions;
  onCreate: (payload: Partial<CustomSurveyReportData>) => void;
}

const EMPTY_FORM: Record<string, unknown> = { split_type: 'DEMOGRAPHIC' };

// Create-report form: name + split (demographic / leader / custom filter).
// A pure form - it hands the transformed payload to onCreate and lets the
// caller own the actual POST, same convention as CustomSurveyEditorModal.
const CustomSurveyReportCreateModal: FC<Props> = ({
  isOpen,
  toggle,
  surveyId,
  splitOptions,
  onCreate,
}) => {
  const { formatMessage } = useIntl();

  const [formObj, setFormObj] = useState<Record<string, unknown>>(EMPTY_FORM);

  // Reset the form to defaults whenever the modal (re)opens. The locked
  // "survey audience" field is seeded here (not in EMPTY_FORM, which is a
  // module-level constant with no access to splitOptions) and never
  // changes afterward - disabled below, same lock pattern as an archived
  // survey's fields (see CustomSurveyBasicsTab).
  useEffect(() => {
    if (isOpen) {
      setFormObj({
        ...EMPTY_FORM,
        survey_audience: splitOptions.effective_target_filter ?? undefined,
      });
    }
  }, [isOpen, splitOptions.effective_target_filter]);

  const splitType = (formObj.split_type as ReportSplitType) ?? 'DEMOGRAPHIC';
  const splitTypeOptions = useMemo(
    () => REPORT_SPLIT_TYPE_OPTIONS(formatMessage),
    [formatMessage]
  );

  const keyOptions = useMemo(() => {
    if (splitType === 'DEMOGRAPHIC') {
      return splitOptions.demographic.map((o) => ({ id: o.key, name: o.name }));
    }
    if (splitType === 'LEADERS') {
      return splitOptions.leader_levels.map((o) => ({
        id: o.key,
        name: o.name,
      }));
    }
    return [];
  }, [splitType, splitOptions]);

  const handleValidate = (obj: Record<string, unknown>) =>
    validateCustomSurveyReport(obj, formatMessage);

  const transformObjectBeforeSubmit = (
    obj: Record<string, unknown>
  ): Partial<CustomSurveyReportData> & { target_audience?: ApiGroup } => ({
    survey: surveyId,
    name: (obj.name as string).trim(),
    split_type: splitType,
    split_key: splitType === 'CUSTOM' ? '' : (obj.split_key as string),
    ...(splitType === 'CUSTOM'
      ? { target_audience: (obj.target_audience as ApiGroup) ?? {} }
      : {}),
  });

  const hasBaseFilter = Boolean(splitOptions.effective_target_filter);

  const inputs = useMemo(
    () => [
      {
        name: 'name',
        type: INPUT_TYPES.TEXT,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.create_modal.name',
          defaultMessage: 'Report name',
        }),
        required: true,
      },
      {
        name: 'split_type',
        // Same input type as the EngagementSurveyReport create modal's
        // slice_type field (radio buttons via MultipleChoiceInput), for
        // consistency across the app's report-creation modals. Each
        // option's `helperText` (not `description`, which MultipleChoiceInput
        // renders as a hover-only popover) shows as always-visible text right
        // below the option - same field EngagementSurveyReportNewModalEditorButton
        // uses for its own slice_type options.
        type: INPUT_TYPES.MULTIPLE_CHOICE,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.create_modal.split_type',
          defaultMessage: 'Split',
        }),
        options: splitTypeOptions.map((o) => ({
          id: o.id,
          name: o.name,
          helperText: o.helperText,
        })),
        required: true,
        // split_key's meaning is entirely different per split_type (a
        // demographic dimension vs. a leader level), so a stale value from
        // the previous type must not survive the switch - e.g. picking
        // "location" under Demographic then switching to Leaders must not
        // submit split_type=LEADERS with split_key="location".
        onChangeSideEffects: (
          currentObject: Record<string, unknown>,
          newValue: string
        ) => {
          if (currentObject.split_type === newValue) return {};
          return { split_key: '', target_audience: undefined };
        },
      },
      // split_key and target_audience stay mounted at all times (never
      // spliced out of `inputs`), just visually hidden via formGroupStyle
      // when irrelevant. ValidatedForm never clears its own stale
      // per-field validation state when an input disappears from the
      // array - only real prop changes (like `required` flipping false)
      // make the underlying component re-validate and clear its own
      // message. Splicing them in/out left a stale "Leader level is
      // required" behind after switching to Custom, since split_key was
      // no longer present to reset it.
      {
        name: 'split_key',
        type: INPUT_TYPES.DROPDOWN,
        label:
          splitType === 'LEADERS'
            ? formatMessage({
                id: 'app.views.administration.custom_surveys.reports.create_modal.leader_level',
                defaultMessage: 'Leader level',
              })
            : formatMessage({
                id: 'app.views.administration.custom_surveys.reports.create_modal.dimension',
                defaultMessage: 'Dimension',
              }),
        objects: keyOptions,
        required: splitType !== 'CUSTOM',
        clearable: false,
        formGroupStyle:
          splitType === 'CUSTOM' ? { display: 'none' } : undefined,
      },
      // Locked, read-only preview of the survey's own ceiling - same lock
      // pattern as a disabled field on an archived survey (AdvancedFilter
      // hides its add/remove-condition controls and disables its selects
      // whenever `disabled` is set, regardless of which survey/context
      // provider renders it). Purely informational: the server ANDs
      // whatever's submitted in `target_audience` below against this same
      // ceiling regardless of what's shown here. Only mounted at all when
      // there's an actual restriction to show - an unrestricted survey has
      // nothing to lock.
      //
      // Unscoped (role USER) suggestions, matching AdminScopeDisclosure,
      // rather than the survey-scoped ones the editable filter below uses:
      // those are narrowed by admin_target_audience itself, and a ceiling of
      // `manager_or_above = <owner>` excludes the owner from its own
      // population - leaving this field with no option to render its value
      // against. It is read-only, so it needs no scoping of its own.
      ...(hasBaseFilter
        ? [
            {
              name: 'survey_audience',
              type: INPUT_TYPES.CUSTOM_COMPONENT,
              component: AdvancedCurrentPeopleFilterRoleUserApiComponent,
              disabled: true,
              label: formatMessage({
                id: 'app.views.administration.custom_surveys.reports.create_modal.survey_audience',
                defaultMessage: 'Survey audience',
              }),
              helperText: formatMessage({
                id: 'app.views.administration.custom_surveys.reports.create_modal.survey_audience_help',
                defaultMessage:
                  'This survey already only reaches this population - the additional filter below narrows within it.',
              }),
              formGroupStyle:
                splitType !== 'CUSTOM' ? { display: 'none' } : undefined,
            },
          ]
        : []),
      {
        name: 'target_audience',
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: AdvancedCustomSurveyAudienceFilterApiComponent,
        surveyId,
        label: hasBaseFilter
          ? formatMessage({
              id: 'app.views.administration.custom_surveys.reports.create_modal.additional_filter',
              defaultMessage: 'Additional filter',
            })
          : formatMessage({
              id: 'app.views.administration.custom_surveys.reports.create_modal.filter',
              defaultMessage: 'Filter',
            }),
        helperText: hasBaseFilter
          ? formatMessage({
              id: 'app.views.administration.custom_surveys.reports.create_modal.filter_help_narrow',
              defaultMessage:
                'Leave empty to include everyone in the survey audience above.',
            })
          : formatMessage({
              id: 'app.views.administration.custom_surveys.reports.create_modal.filter_help',
              defaultMessage: 'Leave empty to include everyone.',
            }),
        formGroupStyle:
          splitType !== 'CUSTOM' ? { display: 'none' } : undefined,
      },
    ],
    [
      formatMessage,
      splitType,
      splitTypeOptions,
      keyOptions,
      surveyId,
      hasBaseFilter,
    ]
  );

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      title={formatMessage({
        id: 'app.views.administration.custom_surveys.reports.create_modal.title',
        defaultMessage: 'Create report',
      })}
      object={formObj}
      inputs={inputs}
      onChange={setFormObj}
      onValidate={handleValidate}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      submitText={formatMessage({
        id: 'app.views.administration.custom_surveys.reports.create_modal.create',
        defaultMessage: 'Create report',
      })}
      disableUnsavedChangesPrompt
      noValidate
      callback={onCreate}
      size="lg"
    />
  );
};

export default CustomSurveyReportCreateModal;
